import { Result } from "../../../shared/api/services/result";
import { AuthApiService } from "../../auth/api/AuthApiService";
import { isMockEnabled, getMockProgress, getMockRequestsList } from "../../../shared/dev/mockData";

// =====================================
// DOCTOR PROGRESS TYPES
// =====================================

export type RequestType = "registration" | "closure" | "relocation" | "renewal" | "onboarding";
export type RequestStatus = "draft" | "submitted" | "in_review" | "preliminary_approved" | "approved" | "rejected" | "cancelled" | "changes_requested";
export type StageStatus = "completed" | "in_progress" | "not_started";

export interface DocumentProgress {
  document_type: string;
  label: string;
  required: boolean;
  uploaded: boolean;
  is_array?: boolean;
  count?: number;
}

export interface StageProgress {
  stage: string;
  label: string;
  status: StageStatus;
}

export interface NextAction {
  action: string;
  document_type?: string;
  route?: string;
}

export interface CorrectionField {
  field_name: string;
  field_label: string;
  field_type: string;
  reason: string;
  current_value?: any;
  target?: string; // "doctor" for profile docs, "request" for request attachments
}

export interface CorrectionsData {
  corrections: CorrectionField[];
  corrections_general_notes: string | null;
  requested_at: string;
}

export interface ActiveRequest {
  request_id: string;
  request_number: string;
  request_type: RequestType;
  status: RequestStatus;
  created_at?: string;
  submitted_at?: string | null;
  next_action?: NextAction;
  documents: DocumentProgress[];
  documents_complete: boolean;
  documents_progress?: number;
  stages: StageProgress[];
  current_stage: string;
  progress_percentage: number;
  governorate: string;
  // Corrections (when status is changes_requested)
  corrections?: CorrectionsData;
}

// Doctor basic info (from /progress, replaces /me)
export interface DoctorInfo {
  id: string;
  full_name: string;
  full_name_ar?: string;
  phone_number?: string;
}

// Signup progress information (replaces /me for profile status)
export interface SignupProgress {
  profile_completed: boolean;
  phone_verified: boolean;
  face_verified: boolean;
  identity_verified: boolean;
  license_verified: boolean;
  documents: DocumentProgress[];
  documents_complete: boolean;
  stages: StageProgress[];
  current_stage: string;
  progress_percentage: number;
}

// Clinic info from progress response
// Mirrors GET /clinic/doctor/profile/my-clinic. Field names must match the API
// exactly — mismatched names render as blank rows instead of failing loudly.
export interface ClinicInfo {
  id: string;
  name: string;
  name_ar?: string | null;
  status: string;
  registration_status?: string;
  license_number?: string | null;
  registration_number?: string | null;
  license_issued_date?: string | null;
  license_expiry_date?: string | null;
  opened_date?: string | null;
  phone_primary?: string | null;
  phone_secondary?: string | null;
  email?: string | null;
  governorate?: string | null;
  city?: string | null;
  district?: string | null;
  neighborhood?: number | string | null;
  street?: number | string | null;
  house_number?: number | string | null;
  nearest_landmark?: string | null;
  address?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  // ساعات العمل — declared on the registration/onboarding request; the API
  // resolves it from the doctor's approved request and returns it here.
  clinic_work_hours?: "morning" | "evening" | null;
  created_at?: string;
  updated_at?: string;
}

export interface DoctorProgressResponse {
  // Doctor basic info
  doctor_info: DoctorInfo;
  // Signup/profile status
  signup: SignupProgress;
  // Clinic status
  has_clinic: boolean;
  clinic_id?: string;
  clinic?: ClinicInfo;
  // Active requests
  has_active_requests: boolean;
  active_requests: ActiveRequest[];
  total_requests: number;
  // Pending actions as simple strings
  pending_actions: string[];
}


/**
 * Modern, clean API service for clinic operations
 * Separate from the legacy RegistrationApiService
 */
export class ClinicApiService {
  private baseURL: string;
  private authApi = AuthApiService.getInstance();

  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
  }

  /**
   * Get doctor's clinic (single clinic only)
   * GET /clinic/doctor/profile/my-clinic
   */
  async getMyClinic(): Promise<Result<any>> {
    if (isMockEnabled()) {
      const mockProgress = getMockProgress();
      return Result.success(mockProgress.clinic || null);
    }

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/profile/my-clinic`
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to get clinic"
        );
      }

      const result = await response.json();
      return Result.success(result.data || null);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get clinic"
      );
    }
  }

  /**
   * Get doctor's clinic requests
   * GET /clinic/doctor/profile/my-all-requests
   */
  async getMyRequests(
    page: number = 1,
    pageSize: number = 20
  ): Promise<Result<{
    total: number;
    page: number;
    page_size: number;
    requests: any[];
  }>> {
    if (isMockEnabled()) {
      return Result.success(getMockRequestsList());
    }

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/profile/my-all-requests?${params}`
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to get requests"
        );
      }

      const result = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get requests"
      );
    }
  }

  /**
   * Cancel a clinic request
   * DELETE /clinic/doctor/profile/my-all-requests/{request_id}
   */
  async cancelRequest(requestId: string): Promise<Result<any>> {
    if (isMockEnabled()) {
      console.log("[Mock] cancelRequest", requestId);
      return Result.success({ message: "Mock cancel successful" });
    }

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/profile/my-all-requests/${requestId}`,
        { method: "DELETE" }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to cancel request"
        );
      }

      const result = await response.json();
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to cancel request"
      );
    }
  }

  // =====================================
  // DOCTOR PROGRESS METHODS
  // =====================================

  /**
   * Get doctor's progress across all active requests
   * GET /doctor/progress
   */
  async getDoctorProgress(): Promise<Result<DoctorProgressResponse>> {
    if (isMockEnabled()) {
      return Result.success(getMockProgress());
    }

    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/profile/progress`
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.detail || error.message || "Failed to get progress"
        );
      }

      const result = await response.json();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get progress"
      );
    }
  }
}
